import React, { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Switch } from "@mui/material";
import { FiltersWrapper, DynamicList, icon, PortfolioInputFilter, DateFilter } from "../../../../../common/components";
import { capitalize, isNonEmptyArray, isNonEmptyObject } from "../../../../../common/utils";
import { useApi } from "../../../../../common/hooks";
import { PortfolioSearchService } from "../../../../../api";
import { translate } from "../../../../../common/providers";
import styles from "./PortfolioTabSearch.module.css";
import { preparePayload } from "../utils/utils";
import { setFilters } from "../slice/portfolio-space-slice";

export default function PortfolioTabSearch({ showArchivedSwitch = true, searchPlaceholder }) {
	const { tableHead, currentTab, myPortfolios, filters } = useSelector(
		({ portfolioSpace }) => ({
			tableHead: portfolioSpace?.tableHead,
			currentTab: portfolioSpace?.currentTab,
			myPortfolios: portfolioSpace?.myPortfolios,
			filters: portfolioSpace?.filters,
		}),
		shallowEqual
	);
	const { call: getMyPortfoliosFiltersByUUIDWithFilters } = useApi(
		PortfolioSearchService.getMyPortfoliosFiltersByUUIDWithFilters
	);
	const [filtersList, setFiltersList] = useState([]);
	const dispatch = useDispatch();
	useEffect(() => {
		setFiltersList(isNonEmptyArray(tableHead) ? tableHead : []);
	}, [tableHead]);
	const handleApply = (newFilters) => {
		dispatch(setFilters(newFilters));
	};
	const handleSwitch = (e) => {
		const value = e.target.checked ? "ARCHIVED" : "ACTIVE";
		dispatch(setFilters({ ...filters, projectStatus: value }));
	};
	const getFilterRenderer = (filter) => {
		if (filter.type === "CATEGORY") {
			return (
				<DynamicList
					identifier="valueUuid"
					labelIdentifier="value"
					request={() =>
						getMyPortfoliosFiltersByUUIDWithFilters(
							{ portfolioId: currentTab.id, uuid: filter.fieldUuid },
							preparePayload(filters)
						)
					}
				/>
			);
		}
		if (filter.type === "DATE_TIME" || filter.type === "DATE" || filter.type === "DATETIME") {
			return <DateFilter type={filter.type} />;
		}
		return <PortfolioInputFilter type={filter.type} />;
	};
	const filtersRenderer = () => {
		if (!isNonEmptyArray(filtersList)) {
			return [];
		}
		return filtersList.map((filter) => ({
			default: false,
			enabled: true,
			hasClearSelection: ["DATE", "DATETIME", "DATE_TIME"].includes(filter.type),
			hasDateSelector: ["DATE", "DATETIME", "DATE_TIME"].includes(filter.type),
			checkDisableApplicable:
				(["DATE", "DATETIME", "DATE_TIME"].includes(filter.type) &&
					((value) => isNonEmptyArray(value) && !value.every((v) => v.value))) ||
				null,
			component: {
				filterKey: filter.fieldUuid,
				label: capitalize(filter?.name),
				iconName: icon.faSearch,
			},
			popUpClassName: ["DATE", "DATETIME", "DATE_TIME"].includes(filter.type) && styles.popup,
			renderer: getFilterRenderer(filter),
		}));
	};
	const portfolioType = useMemo(() => {
		const contract = "dashboards.portfolio-space-archived-contract-switch";
		const certificate = "dashboards.portfolio-space-archived-certificat-switch";
		if (!isNonEmptyObject(currentTab) && myPortfolios?.length === 1) {
			return translate((myPortfolios[0].name === "Contrats" && contract) || certificate);
		}
		if (isNonEmptyObject(currentTab)) {
			return translate((currentTab?.name === "Contrats" && contract) || certificate);
		}
		return "";
	}, [currentTab, myPortfolios]);
	const searchLabel = useMemo(() => {
		if (!isNonEmptyObject(currentTab) && myPortfolios?.length === 1) {
			return (
				(myPortfolios[0].name === "Contrats" && "common:portfolio.search.input.text.label.contract") ||
				"common:portfolio.search.input.text.label.certificate"
			);
		}
		if (isNonEmptyObject(currentTab)) {
			return (
				(currentTab?.name === "Contrats" && "common:portfolio.search.input.text.label.contract") ||
				"common:portfolio.search.input.text.label.certificate"
			);
		}
		return "";
	}, [currentTab, myPortfolios]);
	return (
		<div className={styles.container}>
			<div className={styles.filterContainer}>
				<FiltersWrapper
					hasClearSelection
					hasSearch
					components={filtersRenderer()}
					defaultFilters={filters}
					searchClassName={styles.searchInput}
					searchLabel={searchPlaceholder || translate(searchLabel)}
					onApply={handleApply}
				/>
			</div>
			{showArchivedSwitch && portfolioType && (
				<div className={styles.switchContainer}>
					<Switch checked={filters?.projectStatus === "ARCHIVED"} onChange={handleSwitch} />
					<span>{portfolioType}</span>
				</div>
			)}
		</div>
	);
}
